/**
 * System Information Gathering Utilities for Feedback Collection
 * 
 * Note: These functions were previously used client-side for gathering system info
 * before creating GitHub issues. With the refactored architecture, GitHub issue creation
 * is now server-side only. These functions are kept for potential future use or
 * for displaying system info in the UI, but are not currently used in the main flow.
 * 
 * Server-side issue creation handles system info gathering independently.
 * 
 * Automatically collects comprehensive debugging information including:
 * - Device and browser details
 * - Screen resolution and viewport
 * - Operating system
 * - Console logs (errors and warnings)
 * - Locale and timezone
 * - Network status
 * - Performance metrics
 */

interface ConsoleMessage {
  level: 'error' | 'warn' | 'info' | 'log';
  message: string;
  timestamp: string;
  category?: string;
}

interface SystemInfo {
  // Device & Browser
  userAgent: string;
  browser: string;
  browserVersion: string;
  device: string;
  platform: string;

  // Screen & Display
  screenResolution: string;
  viewportSize: string;
  pixelRatio: number;
  colorDepth: number;

  // Operating System
  os: string;
  osVersion: string;

  // Locale & Time
  locale: string;
  timezone: string;
  timezoneOffset: number;

  // Network & Performance
  connectionType: string;
  connectionSpeed: string;
  online: boolean;

  // App State
  currentUrl: string;
  referrer: string;
  timestamp: string;
  correlationId: string;

  // Console Logs
  recentConsoleLogs: ConsoleMessage[];

  // User Info
  userId?: string;
  userEmail?: string;
}

// Logs are now handled by ActivityLogger
import { getLogs, LogEntry } from "@/lib/activity-logger";
import { detectBrowser } from "@/lib/utils";
import { getCorrelationId } from "@/lib/logging";

// Mapping LogEntry to ConsoleMessage for backward compatibility in this file
const consoleMessages: ConsoleMessage[] = []; // Deprecated shim if needed, or we just map getLogs()

/**
 * Initialize console interceptor to capture messages
 * Deprecated: ActivityLogger handles this.
 */
export function initConsoleInterceptor() {
  // No-op as this is now handled by ActivityLogger when recording starts, 
  // or centrally if one wants always-on logging (which ActivityLogger currently does only on recording).
  // If we want always-on logging for system-info, we might need ActivityLogger to support that mode.
  // For now, consistent with the new requirement "all the activities... are recorded... (if he allowed)",
  // we assume logging starts when the user says so.
  // However, `FeedbackButton` calls `initConsoleInterceptor` on mount. 
  // We can let it be a no-op.
}

function getRecentLogs(): ConsoleMessage[] {
  const logs = getLogs();
  return logs.map(l => ({
    level: l.level,
    message: l.message,
    timestamp: l.timestamp,
    category: l.category
  }));
}

function mapLogsToConsoleMessages(logs: LogEntry[]): ConsoleMessage[] {
  return logs.map(l => ({
    level: l.level,
    message: l.message,
    timestamp: l.timestamp,
    category: l.category
  }));
}

// Local detectBrowser removed, using shared utility

/**
 * Detect device type
 */
function detectDevice(): string {
  const ua = navigator.userAgent;

  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'Android Phone';
  if (/Android/i.test(ua)) return 'Android Tablet';
  if (/Windows Phone/i.test(ua)) return 'Windows Phone';

  // Desktop detection
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobile))/i.test(ua)) {
    return 'Tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'Mobile';
  }

  return 'Desktop';
}

/**
 * Detect operating system and version
 */
function detectOS(): { name: string; version: string } {
  const ua = navigator.userAgent;
  let os = 'Unknown';
  let version = 'Unknown';

  if (/Windows NT 10.0/i.test(ua)) {
    os = 'Windows';
    version = '10 or 11';
  } else if (/Windows NT 6.3/i.test(ua)) {
    os = 'Windows';
    version = '8.1';
  } else if (/Windows NT 6.2/i.test(ua)) {
    os = 'Windows';
    version = '8';
  } else if (/Windows NT 6.1/i.test(ua)) {
    os = 'Windows';
    version = '7';
  } else if (/Windows/i.test(ua)) {
    os = 'Windows';
    version = 'Unknown version';
  } else if (/Mac OS X ([0-9_]+)/i.test(ua)) {
    os = 'macOS';
    const match = ua.match(/Mac OS X ([0-9_]+)/i);
    version = match ? match[1].replace(/_/g, '.') : 'Unknown';
  } else if (/Android ([0-9.]+)/i.test(ua)) {
    os = 'Android';
    version = ua.match(/Android ([0-9.]+)/i)?.[1] || 'Unknown';
  } else if (/iOS|iPhone|iPad|iPod/i.test(ua)) {
    os = 'iOS';
    const match = ua.match(/OS ([0-9_]+)/i);
    version = match ? match[1].replace(/_/g, '.') : 'Unknown';
  } else if (/Linux/i.test(ua)) {
    os = 'Linux';
  } else if (/CrOS/i.test(ua)) {
    os = 'Chrome OS';
  }

  return { name: os, version };
}

/**
 * Get connection information
 */
function getConnectionInfo(): { type: string; speed: string } {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;

  if (!connection) {
    return { type: 'Unknown', speed: 'Unknown' };
  }

  const effectiveType = connection.effectiveType || 'Unknown';
  const downlink = connection.downlink ? `${connection.downlink} Mbps` : 'Unknown';

  return {
    type: effectiveType,
    speed: downlink,
  };
}

/**
 * Gather all system information
 * @param overrideLogs - Optional logs to use instead of ActivityLogger.getLogs() (e.g. from recording session)
 */
export function gatherSystemInfo(userId?: string, userEmail?: string, overrideLogs?: LogEntry[]): SystemInfo {
  const browser = detectBrowser();
  const os = detectOS();
  const device = detectDevice();
  const connection = getConnectionInfo();
  const recentConsoleLogs = overrideLogs ? mapLogsToConsoleMessages(overrideLogs) : getRecentLogs();

  return {
    // Device & Browser
    userAgent: navigator.userAgent,
    browser: browser.name,
    browserVersion: browser.version,
    device,
    platform: navigator.platform,

    // Screen & Display
    screenResolution: `${screen.width}x${screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    pixelRatio: window.devicePixelRatio || 1,
    colorDepth: screen.colorDepth,

    // Operating System
    os: os.name,
    osVersion: os.version,

    // Locale & Time
    locale: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),

    // Network & Performance
    connectionType: connection.type,
    connectionSpeed: connection.speed,
    online: navigator.onLine,

    // App State
    currentUrl: window.location.href,
    referrer: document.referrer || 'Direct',
    timestamp: new Date().toISOString(),
    correlationId: getCorrelationId(),

    // Console Logs (last errors and warnings)
    recentConsoleLogs,

    // User Info
    userId,
    userEmail,
  };
}

/**
 * Format system info as markdown for GitHub issue
 * 
 * @param info - System information object
 * @param options - Formatting options
 * @param options.maxLogs - Maximum number of logs to include (defaults to all)
 * @param options.summary - Summary text for the details tag
 * @param options.open - Whether the details tag should be open by default
 */
export function formatSystemInfoAsMarkdown(
  info: SystemInfo,
  options: { maxLogs?: number; summary?: string; open?: boolean } = {}
): string {
  const { maxLogs, summary = 'Full Session Log (click to collapse)', open = true } = options;
  const sections: string[] = [];

  // Environment Section
  sections.push('## 🖥️ Environment Information');
  sections.push('');
  sections.push('### Device & Browser');
  sections.push(`- **Browser:** ${info.browser} ${info.browserVersion}`);
  sections.push(`- **Device Type:** ${info.device}`);
  sections.push(`- **Platform:** ${info.platform}`);
  sections.push(`- **User Agent:** \`${info.userAgent}\``);
  sections.push('');

  sections.push('### Operating System');
  sections.push(`- **OS:** ${info.os} ${info.osVersion}`);
  sections.push('');

  sections.push('### Display');
  sections.push(`- **Screen Resolution:** ${info.screenResolution}`);
  sections.push(`- **Viewport Size:** ${info.viewportSize}`);
  sections.push(`- **Pixel Ratio:** ${info.pixelRatio}x`);
  sections.push(`- **Color Depth:** ${info.colorDepth}-bit`);
  sections.push('');

  sections.push('### Locale & Time');
  sections.push(`- **Locale:** ${info.locale}`);
  sections.push(`- **Timezone:** ${info.timezone} (UTC${info.timezoneOffset > 0 ? '-' : '+'}${Math.abs(info.timezoneOffset / 60)})`);
  sections.push(`- **Timestamp:** ${info.timestamp}`);
  sections.push('');

  sections.push('### Network');
  sections.push(`- **Online Status:** ${info.online ? '✅ Online' : '❌ Offline'}`);
  sections.push(`- **Connection Type:** ${info.connectionType}`);
  sections.push(`- **Connection Speed:** ${info.connectionSpeed}`);
  sections.push('');

  sections.push('### Page Context');
  sections.push(`- **Correlation ID:** \`${info.correlationId}\``);
  sections.push(`- **Current URL:** ${info.currentUrl}`);
  sections.push(`- **Referrer:** ${info.referrer}`);
  sections.push('');

  // User Information Section
  if (info.userId || info.userEmail) {
    sections.push('### User Information');
    if (info.userId) {
      sections.push(`- **User ID:** \`${info.userId}\``);
    }
    if (info.userEmail) {
      sections.push(`- **Email:** ${info.userEmail}`);
    }
    sections.push('');
  }

  // Console Logs Section
  if (info.recentConsoleLogs.length > 0) {
    const totalLogs = info.recentConsoleLogs.length;
    const logsToDisplay = maxLogs ? info.recentConsoleLogs.slice(-maxLogs) : info.recentConsoleLogs;
    const displayCount = logsToDisplay.length;

    sections.push('## 📝 Activity Log');
    sections.push('');
    if (maxLogs && totalLogs > maxLogs) {
      sections.push(`Showing last ${displayCount} of ${totalLogs} recorded activities...`);
    } else {
      sections.push(`Showing all ${totalLogs} recorded activities...`);
    }
    sections.push('');

    sections.push(`<details${open ? ' open' : ''}>`);
    sections.push(`<summary>${summary}</summary>`);
    sections.push('');
    sections.push('```');

    logsToDisplay.forEach(log => {
      let emoji = 'ℹ️';
      if (log.level === 'error') emoji = '❌';
      else if (log.level === 'warn') emoji = '⚠️';
      else if (log.category === 'ui') emoji = '🖱️';
      else if (log.category === 'network') emoji = '🌐';
      else if (log.category === 'auth') emoji = '🔐';

      const categoryTag = log.category ? `[${log.category.toUpperCase()}]` : '';
      sections.push(`[${log.timestamp}] ${emoji} ${categoryTag} ${log.message}`);
    });

    sections.push('```');
    sections.push('</details>');
    sections.push('');
  }

  return sections.join('\n');
}

/**
 * Get a formatted summary of system info for display
 */
export function getSystemInfoSummary(info: SystemInfo): string {
  return `${info.device} • ${info.browser} ${info.browserVersion} • ${info.os} ${info.osVersion} • ${info.screenResolution}`;
}

